var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { animated, useSpring, useSpringRef, useChain } from '@react-spring/web';
import { darken } from 'polished';
import cx from 'classnames';
import { ProgressCircleContext, useProgressCircle, defaultConfig } from './progress-circle-context';
import { useTheme } from '../theme/theme-context';
import { color } from '../theme/color';
const StyledProgressCircle = styled.svg `
  box-sizing: content-box;
  transform: rotate(-90deg);
  border-radius: 100%;
  .progress-circle {
    position: absolute;
    top: 0px;
    left: 0px;
  }
`;
export const ProgressCircle = (_a) => {
    var { percent = 0, index = 0, stroke = color.light.primary } = _a, props = __rest(_a, ["percent", "index", "stroke"]);
    const config = useProgressCircle();
    const size = index === 0 ? config.size : config.size - config.strokeWidth * (index + 1) - 2;
    const r = size / 2 - config.strokeWidth;
    const perimeter = 2 * r * Math.PI;
    const { theme } = useTheme();
    const [remainOne, remainTwo] = useMemo(() => {
        const level = Math.floor(percent);
        if (level >= 1) {
            return [0, 1 - (percent - level)];
        }
        return [1 - percent, 1];
    }, [percent]);
    const circleOne = useSpringRef();
    const circleOneStyles = useSpring({
        ref: circleOne,
        strokeDashoffset: remainOne * perimeter,
    });
    const circleTwo = useSpringRef();
    const circleTwoStyles = useSpring({
        ref: circleTwo,
        strokeDashoffset: remainTwo * perimeter,
    });
    useChain([circleOne, circleTwo]);
    const pairs = useMemo(() => {
        let light = theme.color.bgColor1;
        let dark = stroke;
        const level = Math.floor(percent);
        if (level === 0) {
            return { light: dark, dark };
        }
        if (percent - level > 0 && level >= 1) {
            light = darken((level - 1) / 10, stroke);
            dark = darken(level / 10, stroke);
        }
        return { light, dark };
    }, [stroke, percent, theme.color.bgColor1]);
    return (React.createElement(React.Fragment, null,
        React.createElement(animated.circle, { className: cx('progress-circle', props.className), stroke: theme.color.bgColor1, cx: "50%", cy: "50%", r: r, strokeDasharray: perimeter, "data-role": "progress-circle" }),
        React.createElement(animated.circle, Object.assign({ className: cx('progress-circle', props.className), stroke: pairs.light, cx: "50%", cy: "50%", r: r, strokeDasharray: perimeter }, circleOneStyles, { style: { filter: 'url(#dropshadow)' }, "data-role": "progress-circle" })),
        React.createElement(animated.circle, Object.assign({ className: cx('progress-circle', 'progress-circle-inner', props.className), stroke: pairs.dark, cx: "50%", cy: "50%", r: r, strokeDasharray: perimeter }, circleTwoStyles, { style: { filter: 'url(#dropshadow)' }, "data-role": "progress-circle" }))));
};
export const ProgressCircleGroups = (_a) => {
    var { size = 50, strokeWidth = 5 } = _a, props = __rest(_a, ["size", "strokeWidth"]);
    const children = React.Children.toArray(props.children);
    return (React.createElement(ProgressCircleContext.Provider, { value: Object.assign(Object.assign({}, defaultConfig), { size, strokeWidth }) },
        React.createElement(StyledProgressCircle, { width: size, height: size, strokeWidth: strokeWidth, strokeLinecap: "round", onClick: props.onClick, fill: "none", className: cx('progress-circle-container', props.className) },
            React.createElement("defs", null,
                React.createElement("filter", { id: "dropshadow", x: "0", y: "0", width: "180%", height: "180%", filterUnits: "userSpaceOnUse" },
                    React.createElement("feDropShadow", { dx: "0", dy: "0", stdDeviation: "0.5", floodColor: "rgb(0, 0, 0)", floodOpacity: "0.5" }),
                    React.createElement("feMerge", null,
                        React.createElement("feMergeNode", null),
                        React.createElement("feMergeNode", { in: "SourceGraphic" }),
                        React.createElement("feMergeNode", { in: "SourceGraphic" })))),
            children.map((element, index) => {
                if (typeof element !== 'object' || !element) {
                    return element;
                }
                if (!('type' in element)) {
                    return element;
                }
                return React.createElement(element.type, Object.assign({}, element.props, { index: index }));
            }))));
};
